import { useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { isLegacyPath } from "@/lib/theme";
import { SuiteShell } from "@/components/shell/SuiteShell";
import { AuthProvider } from "@/context/AuthContext";
import { ChatProvider } from "@/context/ChatContext";
import { ChatWidget } from "@/components/ChatWidget";
import { ProtectedRoute, ModuleRoute } from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AppView from "@/pages/AppView";
import Admin from "@/pages/Admin";
import Anagrafiche from "@/pages/Anagrafiche";
import AnagraficheDoppioni from "@/pages/AnagraficheDoppioni";
import ProspectTerzoSettore from "@/pages/ProspectTerzoSettore";
import PrototipoAdesione from "@/pages/PrototipoAdesione";
import InvitiAdesione from "@/pages/adesione/InvitiAdesione";
import AdesioniOnline from "@/pages/adesione/AdesioniOnline";
import { AdesioneInvito, AdesioneConferma, AdesioneAccesso, AdesionePassword, AdesioneReset, AdesioneArea } from "@/pages/adesione/Adesione";
import CauDashboard from "@/pages/cauzioni/CauDashboard";
import CauWizard from "@/pages/cauzioni/CauWizard";
import CauRelazione from "@/pages/cauzioni/CauRelazione";
import CauSettings from "@/pages/cauzioni/CauSettings";
import AbiDashboard from "@/pages/abi/AbiDashboard";
import AbiAnalysis from "@/pages/abi/AbiAnalysis";
import AbiFonti from "@/pages/abi/AbiFonti";
import AbiTerzi from "@/pages/abi/AbiTerzi";
import BiPortal from "@/pages/BiPortal";
import AbiQuestionario from "@/pages/abi/AbiQuestionario";
import RicevutaDocumenti from "@/pages/abi/RicevutaDocumenti";
import AbiCambiamenti from "@/pages/abi/AbiCambiamenti";
import SignDashboard from "@/pages/sign/SignDashboard";
import SignIdentities from "@/pages/sign/SignIdentities";
import SignDocuments from "@/pages/sign/SignDocuments";
import SignTransactions from "@/pages/sign/SignTransactions";
import SignPublic from "@/pages/sign/SignPublic";
import SignFormPublic from "@/pages/sign/SignFormPublic";
import SignQuestionari from "@/pages/sign/SignQuestionari";
import SignConsensi from "@/pages/sign/SignConsensi";
import SignConsentPublic from "@/pages/sign/SignConsentPublic";
import FFPublic from "@/pages/ff/FFPublic";
import FFAdmin from "@/pages/ff/FFAdmin";
import DirezioneDashboard from "@/pages/direzione/DirezioneDashboard";
import ClienteDetail from "@/pages/ClienteDetail";
import WorkInProgress from "@/pages/WorkInProgress";
import ProgettiBenefit from "@/pages/ProgettiBenefit";
import GestioneAgenzia from "@/pages/GestioneAgenzia";
import SezioneContenitore from "@/pages/SezioneContenitore";
import PortalePartnerBridge from "@/pages/PortalePartnerBridge";
import Personale from "@/pages/Personale";
import Compliance from "@/pages/Compliance";
import RegistroBenefit from "@/pages/benefit/RegistroBenefit";
import ChiFaCosa from "@/pages/ChiFaCosa";
import Contabilita from "@/pages/Contabilita";
import PrimaNotaCassa from "@/pages/PrimaNotaCassa";
import ManualeContabilita from "@/pages/manuali/ManualeContabilita";
import ManualePersonale from "@/pages/manuali/ManualePersonale";
import ManualeCompliance from "@/pages/manuali/ManualeCompliance";
import ManualeChiFaCosa from "@/pages/manuali/ManualeChiFaCosa";
import ManualeGestioneAgenzia from "@/pages/manuali/ManualeGestioneAgenzia";
import ManualeMup from "@/pages/manuali/ManualeMup";
import ManualeCauzioni from "@/pages/manuali/ManualeCauzioni";
import ManualeChat from "@/pages/manuali/ManualeChat";
import ManualeFoglioCassa from "@/pages/manuali/ManualeFoglioCassa";
import ManualePrimaNota from "@/pages/manuali/ManualePrimaNota";
import ManualeEasyTrasporto from "@/pages/manuali/ManualeEasyTrasporto";
import ManualeDirezione from "@/pages/manuali/ManualeDirezione";
import ManualeSign from "@/pages/manuali/ManualeSign";
import ManualeBusinessIntelligence from "@/pages/manuali/ManualeBusinessIntelligence";
import Sinistri from "@/pages/Sinistri";
import ModuliSinistri from "@/pages/ModuliSinistri";
import SinistroDetail from "@/pages/SinistroDetail";
import RubricaIncaricati from "@/pages/RubricaIncaricati";
import ArchivioNovita from "@/pages/ArchivioNovita";
import MupDashboard from "@/pages/mup/MupDashboard";
import MupWizard from "@/pages/mup/MupWizard";
import MupPratiche from "@/pages/mup/MupPratiche";
import MupPraticaDetail from "@/pages/mup/MupPraticaDetail";
import MupFirmaPubblica from "@/pages/mup/MupFirmaPubblica";
import Trattative from "@/pages/trattative/Trattative";
import InformativaPubblica from "@/pages/trattative/InformativaPubblica";
import VideoCliente from "@/pages/trattative/video/VideoCliente";
import AreaCliente from "@/pages/trattative/AreaCliente";
import ScadenzarioRecessi from "@/pages/trattative/ScadenzarioRecessi";
import StatoPratiche from "@/pages/trattative/StatoPratiche";
import VideoOperatore from "@/pages/trattative/video/VideoOperatore";
import TrattativaDetail from "@/pages/trattative/TrattativaDetail";
import MupAppendiceFirma from "@/pages/mup/MupAppendiceFirma";
import MupCatalogo from "@/pages/mup/MupCatalogo";
import AreaDashboard from "@/pages/area/AreaDashboard";
import AreaDetail from "@/pages/area/AreaDetail";
import AreaGestione from "@/pages/area/AreaGestione";
import ETDashboard from "@/pages/et/ETDashboard";
import ETBeneficiari from "@/pages/et/ETBeneficiari";
import ETQuote from "@/pages/et/ETQuote";
import ETRicevute from "@/pages/et/ETRicevute";
import { ETConfig, ETLog } from "@/pages/et/ETConfig";
import RCDashboard from "@/pages/rc/RCDashboard";
import RCNewQuote from "@/pages/rc/RCNewQuote";
import RCQuotes from "@/pages/rc/RCQuotes";
import RCQuoteDetail from "@/pages/rc/RCQuoteDetail";
import RCOrganizations from "@/pages/rc/RCOrganizations";
import RCEligibility from "@/pages/rc/RCEligibility";
import MRVDashboard from "@/pages/mrv/MRVDashboard";
import MRVNewQuote from "@/pages/mrv/MRVNewQuote";
import MRVQuotes from "@/pages/mrv/MRVQuotes";
import MRVQuoteDetail from "@/pages/mrv/MRVQuoteDetail";
import MRVOrganizations from "@/pages/mrv/MRVOrganizations";
import ChatInterna from "@/pages/ChatInterna";
import FoglioCassa from "@/pages/FoglioCassa";
import SmartImport from "@/pages/ai/SmartImport";
import AssistenteAI from "@/pages/ai/AssistenteAI";
import { Toaster } from "@/components/ui/sonner";

function AppRouter() {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.classList.toggle("theme-legacy", isLegacyPath(location.pathname));
  }, [location.pathname]);
  // Process OAuth session_id synchronously before any protected route runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <SuiteShell>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/q/:token" element={<AbiQuestionario />} />
      <Route path="/ricevuta/:token" element={<RicevutaDocumenti />} />
      <Route path="/bi-portal" element={<BiPortal />} />
      <Route path="/sign/:token" element={<SignPublic />} />
      <Route path="/sign-form/:token" element={<SignFormPublic />} />
      <Route path="/consenso/:token" element={<SignConsentPublic />} />
      <Route path="/fabbisogno-formativo" element={<FFPublic />} />
      <Route path="/mup/appendice/firma/:token" element={<MupAppendiceFirma />} />
      <Route path="/mup/firma/:token" element={<MupFirmaPubblica />} />
      <Route path="/informativa/:token" element={<InformativaPubblica />} />
      <Route path="/videochiamata/:token" element={<VideoCliente />} />
      <Route path="/adesione/invito/:token" element={<AdesioneInvito />} />
      <Route path="/adesione/conferma/:token" element={<AdesioneConferma />} />
      <Route path="/adesione/accesso" element={<AdesioneAccesso />} />
      <Route path="/adesione/password" element={<AdesionePassword />} />
      <Route path="/adesione/reset/:token" element={<AdesioneReset />} />
      <Route path="/adesione/area" element={<AdesioneArea />} />
      <Route path="/trattative/inviti-adesione" element={<ModuleRoute internalOnly><InvitiAdesione /></ModuleRoute>} />
      <Route path="/trattative/adesioni" element={<ModuleRoute internalOnly><AdesioniOnline /></ModuleRoute>} />
      <Route path="/area/:token" element={<AreaCliente />} />
      <Route path="/fabbisogno-formativo/admin" element={<FFAdmin />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/:appId"
        element={
          <ProtectedRoute>
            <AppView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route path="/novita" element={<ModuleRoute internalOnly><ArchivioNovita /></ModuleRoute>} />
      <Route path="/work-in-progress" element={<ModuleRoute internalOnly><WorkInProgress /></ModuleRoute>} />
      <Route path="/progetti-benefit" element={<ModuleRoute section="benefit"><ProgettiBenefit /></ModuleRoute>} />
      <Route path="/gestione-agenzia" element={<ModuleRoute section="gestione_agenzia"><GestioneAgenzia /></ModuleRoute>} />
      <Route path="/strumenti-digitali" element={<ModuleRoute section="strumenti_digitali"><SezioneContenitore sectionKey="strumenti_digitali" /></ModuleRoute>} />
      <Route path="/contabilita-cooperativa" element={<ModuleRoute section="contabilita_coop"><SezioneContenitore sectionKey="contabilita_coop" /></ModuleRoute>} />
      <Route path="/direzionale" element={<ModuleRoute section="direzionale"><SezioneContenitore sectionKey="direzionale" /></ModuleRoute>} />
      <Route path="/portale-prodotti" element={<ModuleRoute moduleKey="/portale-prodotti"><PortalePartnerBridge /></ModuleRoute>} />
      <Route path="/personale" element={<ModuleRoute moduleKey="/personale"><Personale /></ModuleRoute>} />
      <Route path="/compliance" element={<ModuleRoute moduleKey="/compliance"><Compliance /></ModuleRoute>} />
      <Route path="/registro-benefit" element={<ModuleRoute moduleKey="/registro-benefit"><RegistroBenefit /></ModuleRoute>} />
      <Route path="/chi-fa-cosa" element={<ModuleRoute moduleKey="/chi-fa-cosa"><ChiFaCosa /></ModuleRoute>} />
      <Route path="/contabilita" element={<ModuleRoute moduleKey="/contabilita"><Contabilita /></ModuleRoute>} />
      <Route path="/prima-nota" element={<ModuleRoute moduleKey="/prima-nota"><PrimaNotaCassa /></ModuleRoute>} />
      <Route path="/smart-import" element={<ProtectedRoute><SmartImport /></ProtectedRoute>} />
      <Route path="/trattative" element={<ProtectedRoute><Trattative /></ProtectedRoute>} />
      <Route path="/trattative/stato" element={<ProtectedRoute><StatoPratiche /></ProtectedRoute>} />
      <Route path="/trattative/recessi" element={<ProtectedRoute><ScadenzarioRecessi /></ProtectedRoute>} />
      <Route path="/trattative/:id" element={<ProtectedRoute><TrattativaDetail /></ProtectedRoute>} />
      <Route path="/trattative/:id/video/:sid" element={<ProtectedRoute><VideoOperatore /></ProtectedRoute>} />
      <Route path="/assistente-ai" element={<ProtectedRoute><AssistenteAI /></ProtectedRoute>} />
      <Route path="/manuale/contabilita" element={<ProtectedRoute><ManualeContabilita /></ProtectedRoute>} />
      <Route path="/manuale/personale" element={<ProtectedRoute><ManualePersonale /></ProtectedRoute>} />
      <Route path="/manuale/compliance" element={<ProtectedRoute><ManualeCompliance /></ProtectedRoute>} />
      <Route path="/manuale/chi-fa-cosa" element={<ProtectedRoute><ManualeChiFaCosa /></ProtectedRoute>} />
      <Route path="/manuale/gestione-agenzia" element={<ProtectedRoute><ManualeGestioneAgenzia /></ProtectedRoute>} />
      <Route path="/manuale/mup" element={<ProtectedRoute><ManualeMup /></ProtectedRoute>} />
      <Route path="/manuale/cauzioni" element={<ProtectedRoute><ManualeCauzioni /></ProtectedRoute>} />
      <Route path="/manuale/chat" element={<ProtectedRoute><ManualeChat /></ProtectedRoute>} />
      <Route path="/manuale/foglio-cassa" element={<ProtectedRoute><ManualeFoglioCassa /></ProtectedRoute>} />
      <Route path="/manuale/prima-nota" element={<ProtectedRoute><ManualePrimaNota /></ProtectedRoute>} />
      <Route path="/manuale/easytrasporto" element={<ProtectedRoute><ManualeEasyTrasporto /></ProtectedRoute>} />
      <Route path="/manuale/dashboard-direzionale" element={<ProtectedRoute><ManualeDirezione /></ProtectedRoute>} />
      <Route path="/manuale/assietica-sign" element={<ProtectedRoute><ManualeSign /></ProtectedRoute>} />
      <Route path="/manuale/business-intelligence" element={<ProtectedRoute><ManualeBusinessIntelligence /></ProtectedRoute>} />
      <Route path="/gestione-sinistri" element={<ModuleRoute moduleKey="/gestione-sinistri"><Sinistri /></ModuleRoute>} />
      <Route path="/gestione-sinistri/moduli" element={<ModuleRoute moduleKey="/gestione-sinistri"><ModuliSinistri /></ModuleRoute>} />
      <Route path="/gestione-sinistri/rubrica" element={<ModuleRoute moduleKey="/gestione-sinistri"><RubricaIncaricati /></ModuleRoute>} />
      <Route path="/gestione-sinistri/:id" element={<ModuleRoute moduleKey="/gestione-sinistri"><SinistroDetail /></ModuleRoute>} />
      <Route path="/mup" element={<ModuleRoute moduleKey="/mup"><MupDashboard /></ModuleRoute>} />
      <Route path="/mup/nuova" element={<ModuleRoute moduleKey="/mup"><MupWizard /></ModuleRoute>} />
      <Route path="/mup/pratiche" element={<ModuleRoute moduleKey="/mup"><MupPratiche /></ModuleRoute>} />
      <Route path="/mup/pratiche/:id" element={<ModuleRoute moduleKey="/mup"><MupPraticaDetail /></ModuleRoute>} />
      <Route path="/mup/pratiche/:id/modifica" element={<ModuleRoute moduleKey="/mup"><MupWizard /></ModuleRoute>} />
      <Route path="/mup/catalogo" element={<ProtectedRoute adminOnly><MupCatalogo /></ProtectedRoute>} />
      <Route path="/area-riservata" element={<ModuleRoute moduleKey="/area-riservata"><AreaDashboard /></ModuleRoute>} />
      <Route path="/area-riservata/gestione" element={<ProtectedRoute adminOnly><AreaGestione /></ProtectedRoute>} />
      <Route path="/area-riservata/:id" element={<ModuleRoute moduleKey="/area-riservata"><AreaDetail /></ModuleRoute>} />
      <Route path="/easytrasporto" element={<ModuleRoute moduleKey="/easytrasporto"><ETDashboard /></ModuleRoute>} />
      <Route path="/easytrasporto/beneficiari" element={<ModuleRoute moduleKey="/easytrasporto"><ETBeneficiari /></ModuleRoute>} />
      <Route path="/easytrasporto/quote" element={<ModuleRoute moduleKey="/easytrasporto"><ETQuote /></ModuleRoute>} />
      <Route path="/easytrasporto/ricevute" element={<ModuleRoute moduleKey="/easytrasporto"><ETRicevute /></ModuleRoute>} />
      <Route path="/easytrasporto/config" element={<ProtectedRoute adminOnly><ETConfig /></ProtectedRoute>} />
      <Route path="/easytrasporto/log" element={<ProtectedRoute adminOnly><ETLog /></ProtectedRoute>} />
      <Route path="/quotatore-rc" element={<ModuleRoute moduleKey="/quotatore-rc"><RCDashboard /></ModuleRoute>} />
      <Route path="/quotatore-rc/preventivi" element={<ModuleRoute moduleKey="/quotatore-rc"><RCQuotes /></ModuleRoute>} />
      <Route path="/quotatore-rc/preventivi/nuovo" element={<ModuleRoute moduleKey="/quotatore-rc"><RCNewQuote /></ModuleRoute>} />
      <Route path="/quotatore-rc/preventivi/:id" element={<ModuleRoute moduleKey="/quotatore-rc"><RCQuoteDetail /></ModuleRoute>} />
      <Route path="/quotatore-rc/preventivi/:id/modifica" element={<ModuleRoute moduleKey="/quotatore-rc"><RCNewQuote /></ModuleRoute>} />
      <Route path="/quotatore-rc/enti" element={<ModuleRoute moduleKey="/quotatore-rc"><RCOrganizations /></ModuleRoute>} />
      <Route path="/quotatore-rc/ammissibilita" element={<ModuleRoute moduleKey="/quotatore-rc"><RCEligibility /></ModuleRoute>} />
      <Route path="/multirischi-volontariato" element={<ModuleRoute moduleKey="/multirischi-volontariato"><MRVDashboard /></ModuleRoute>} />
      <Route path="/multirischi-volontariato/preventivi" element={<ModuleRoute moduleKey="/multirischi-volontariato"><MRVQuotes /></ModuleRoute>} />
      <Route path="/multirischi-volontariato/preventivi/nuovo" element={<ModuleRoute moduleKey="/multirischi-volontariato"><MRVNewQuote /></ModuleRoute>} />
      <Route path="/multirischi-volontariato/preventivi/:id" element={<ModuleRoute moduleKey="/multirischi-volontariato"><MRVQuoteDetail /></ModuleRoute>} />
      <Route path="/multirischi-volontariato/preventivi/:id/modifica" element={<ModuleRoute moduleKey="/multirischi-volontariato"><MRVNewQuote /></ModuleRoute>} />
      <Route path="/multirischi-volontariato/enti" element={<ModuleRoute moduleKey="/multirischi-volontariato"><MRVOrganizations /></ModuleRoute>} />
      <Route path="/anagrafiche" element={<ModuleRoute internalOnly><Anagrafiche /></ModuleRoute>} />
      <Route path="/prototipo/adesione-online" element={<ModuleRoute internalOnly><PrototipoAdesione /></ModuleRoute>} />
      <Route path="/anagrafiche/doppioni" element={<ProtectedRoute adminOnly><AnagraficheDoppioni /></ProtectedRoute>} />
      <Route path="/anagrafiche/prospect-terzo-settore" element={<ModuleRoute internalOnly><ProspectTerzoSettore /></ModuleRoute>} />
      <Route path="/cauzioni-pro" element={<ModuleRoute moduleKey="/cauzioni-pro"><CauDashboard /></ModuleRoute>} />
      <Route path="/cauzioni-pro/pratica/:id" element={<ModuleRoute moduleKey="/cauzioni-pro"><CauWizard /></ModuleRoute>} />
      <Route path="/cauzioni-pro/pratica/:id/relazione" element={<ModuleRoute moduleKey="/cauzioni-pro"><CauRelazione /></ModuleRoute>} />
      <Route path="/cauzioni-pro/impostazioni" element={<ModuleRoute moduleKey="/cauzioni-pro"><CauSettings /></ModuleRoute>} />
      <Route path="/business-intelligence" element={<ModuleRoute moduleKey="/business-intelligence"><AbiDashboard /></ModuleRoute>} />
      <Route path="/business-intelligence/fonti" element={<ModuleRoute moduleKey="/business-intelligence"><AbiFonti /></ModuleRoute>} />
      <Route path="/business-intelligence/cambiamenti" element={<ModuleRoute moduleKey="/business-intelligence"><AbiCambiamenti /></ModuleRoute>} />
      <Route path="/business-intelligence/terzi" element={<ModuleRoute moduleKey="/business-intelligence"><AbiTerzi /></ModuleRoute>} />
      <Route path="/business-intelligence/analisi/:id" element={<ModuleRoute moduleKey="/business-intelligence"><AbiAnalysis /></ModuleRoute>} />
      <Route path="/assietica-sign" element={<ModuleRoute moduleKey="/assietica-sign"><SignDashboard /></ModuleRoute>} />
      <Route path="/assietica-sign/clienti" element={<ModuleRoute moduleKey="/assietica-sign"><SignIdentities /></ModuleRoute>} />
      <Route path="/assietica-sign/documenti" element={<ModuleRoute moduleKey="/assietica-sign"><SignDocuments /></ModuleRoute>} />
      <Route path="/assietica-sign/firme" element={<ModuleRoute moduleKey="/assietica-sign"><SignTransactions /></ModuleRoute>} />
      <Route path="/assietica-sign/questionari" element={<ModuleRoute moduleKey="/assietica-sign"><SignQuestionari /></ModuleRoute>} />
      <Route path="/assietica-sign/consensi" element={<ModuleRoute moduleKey="/assietica-sign"><SignConsensi /></ModuleRoute>} />
      <Route path="/dashboard-direzionale" element={<ModuleRoute moduleKey="/dashboard-direzionale"><DirezioneDashboard /></ModuleRoute>} />
      <Route path="/cliente/:id" element={<ModuleRoute internalOnly><ClienteDetail /></ModuleRoute>} />
      <Route path="/chat" element={<ModuleRoute internalOnly><ChatInterna /></ModuleRoute>} />
      <Route path="/foglio-cassa" element={<ModuleRoute moduleKey="/foglio-cassa"><FoglioCassa /></ModuleRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </SuiteShell>
  );
}

function App() {
  return (
    <AuthProvider>
      <ChatProvider>
        <BrowserRouter>
          <AppRouter />
          <ChatWidget />
          <Toaster position="top-right" richColors theme="dark" />
        </BrowserRouter>
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;
